/**
 * Design B, every page. No GSAP here: the header services menu, the phone
 * book bar and the media cursor label must work on templates that never load
 * the motion stack.
 */

/* ---------- Services mega-menu ---------- */

if (!customElements.get('clinic-services')) {
  customElements.define(
    'clinic-services',
    class ClinicServices extends HTMLElement {
      connectedCallback() {
        this.trigger = this.querySelector('.clinic-services__trigger');
        this.panel = this.querySelector('.clinic-services__panel');
        if (!this.trigger || !this.panel) return;

        // The panel spans the header row, so it must be positioned against
        // the header, not against this small trigger.
        this.header = this.closest('header-component') || this.closest('.header__row');

        // On a fine pointer, hovering opens the panel just before the click
        // lands; that click must not immediately close it again.
        this.onTrigger = () => {
          if (this.isOpen && performance.now() - (this.openedAt || 0) < 500) return;
          this.toggle();
        };
        this.onKey = (event) => {
          if (event.key === 'Escape' && this.isOpen) {
            this.close();
            this.trigger.focus();
          }
        };
        this.onDocClick = (event) => {
          if (this.isOpen && !this.contains(event.target)) this.close();
        };
        this.onEnter = () => {
          clearTimeout(this.leaveTimer);
          this.open();
        };
        this.onLeave = () => {
          this.leaveTimer = setTimeout(() => this.close(), 220);
        };

        this.trigger.addEventListener('click', this.onTrigger);
        document.addEventListener('keydown', this.onKey);
        document.addEventListener('click', this.onDocClick);
        if (matchMedia('(hover: hover) and (pointer: fine)').matches) {
          this.addEventListener('pointerenter', this.onEnter);
          this.addEventListener('pointerleave', this.onLeave);
        }
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this.onKey);
        document.removeEventListener('click', this.onDocClick);
      }

      get isOpen() {
        return this.trigger?.getAttribute('aria-expanded') === 'true';
      }

      open() {
        if (this.isOpen) return;
        this.openedAt = performance.now();
        this.trigger.setAttribute('aria-expanded', 'true');
        this.panel.hidden = false;
        this.toggleAttribute('data-open', true);
      }

      close() {
        if (!this.isOpen) return;
        this.trigger.setAttribute('aria-expanded', 'false');
        this.panel.hidden = true;
        this.removeAttribute('data-open');
      }

      toggle() {
        this.isOpen ? this.close() : this.open();
      }
    }
  );
}

/* ---------- Phone book bar ---------- */

function initBookbar() {
  const bar = document.querySelector('[data-clinic-bookbar]');
  if (!bar || !('IntersectionObserver' in window)) return;

  // Visible once the first section has scrolled away, hidden again while the
  // footer is on screen so it never sits on top of the footer links.
  const first = document.querySelector('#MainContent > .shopify-section');
  const footer = document.querySelector('footer');
  let pastFirst = !first;
  let footerInView = false;
  const sync = () => bar.toggleAttribute('data-visible', pastFirst && !footerInView);

  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.target === first) pastFirst = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      if (entry.target === footer) footerInView = entry.isIntersecting;
    });
    sync();
  });
  if (first) io.observe(first);
  if (footer) io.observe(footer);
  sync();
}

/* ---------- "View" cursor label on media, fine pointers only ---------- */

function initCursor() {
  if (!matchMedia('(hover: hover) and (pointer: fine)').matches) return;
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const label = document.createElement('div');
  label.className = 'clinic-cursor';
  label.setAttribute('aria-hidden', 'true');
  document.body.append(label);

  let x = 0;
  let y = 0;
  let raf = 0;
  const paint = () => {
    raf = 0;
    label.style.left = `${x}px`;
    label.style.top = `${y}px`;
  };

  document.addEventListener(
    'pointermove',
    (event) => {
      x = event.clientX;
      y = event.clientY;
      if (!raf) raf = requestAnimationFrame(paint);
      const target = event.target instanceof Element ? event.target.closest('[data-cursor]') : null;
      if (target) {
        label.textContent = target.getAttribute('data-cursor');
        label.toggleAttribute('data-on', true);
      } else {
        label.removeAttribute('data-on');
      }
    },
    { passive: true }
  );
  document.addEventListener('pointerleave', () => label.removeAttribute('data-on'));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initBookbar();
    initCursor();
  });
} else {
  initBookbar();
  initCursor();
}
